import React, { useState } from 'react';
import { Button, Card, Input, Badge, Alert, Grid, Spacing } from './index';
import './DesignSystemDemo.css';

const DesignSystemDemo = () => {
  const [inputValue, setInputValue] = useState('');
  const [showAlert, setShowAlert] = useState(true);

  return (
    <div className="design-system-demo">
      <div className="demo-section">
        <h1>Design System Demo</h1>
        <p>This is a comprehensive showcase of our design system components.</p>
      </div>

      {/* Color Palette */}
      <div className="demo-section">
        <h2>Color Palette</h2>
        <div className="color-grid">
          <div className="color-swatch" style={{ backgroundColor: '#2CA6A4' }}>
            <span>Primary</span>
            <span>#2CA6A4</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#667BC6' }}>
            <span>Secondary</span>
            <span>#667BC6</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#F7F9FA' }}>
            <span>Background</span>
            <span>#F7F9FA</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#2A2A2A' }}>
            <span>Text</span>
            <span>#2A2A2A</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#FFD66B' }}>
            <span>Accent</span>
            <span>#FFD66B</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#3DBE80' }}>
            <span>Success</span>
            <span>#3DBE80</span>
          </div>
          <div className="color-swatch" style={{ backgroundColor: '#E87070' }}>
            <span>Error</span>
            <span>#E87070</span>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="demo-section">
        <h2>Typography</h2>
        <div className="typography-demo">
          <h1>Heading 1 - 32px Bold</h1>
          <h2>Heading 2 - 24px Semibold</h2>
          <p>Body text - 16px Regular. This is how body text looks with proper line height and spacing.</p>
          <p className="text-caption">Caption text - 14px Medium</p>
        </div>
      </div>

      {/* Buttons */}
      <div className="demo-section">
        <h2>Buttons</h2>
        <div className="button-grid">
          <div className="button-group">
            <h3>Variants</h3>
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="accent">Accent</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="success">Success</Button>
            <Button variant="error">Error</Button>
          </div>
          <div className="button-group">
            <h3>Sizes</h3>
            <Button size="small">Small</Button>
            <Button size="medium">Medium</Button>
            <Button size="large">Large</Button>
          </div>
          <div className="button-group">
            <h3>States</h3>
            <Button>Normal</Button>
            <Button disabled>Disabled</Button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="demo-section">
        <h2>Cards</h2>
        <div className="card-grid">
          <Card variant="default" padding="medium">
            <h3>Default Card</h3>
            <p>This is a default card with medium padding.</p>
          </Card>
          <Card variant="elevated" padding="medium">
            <h3>Elevated Card</h3>
            <p>This card has more shadow for emphasis.</p>
          </Card>
          <Card variant="outlined" padding="medium">
            <h3>Outlined Card</h3>
            <p>This card has a primary border.</p>
          </Card>
          <Card variant="accent" padding="medium">
            <h3>Accent Card</h3>
            <p>This card uses the accent color.</p>
          </Card>
        </div>
      </div>

      {/* Inputs */}
      <div className="demo-section">
        <h2>Inputs</h2>
        <div className="input-grid">
          <Input 
            label="Default Input"
            placeholder="Enter text here..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Input 
            label="Success Input"
            placeholder="This input is successful"
            success
            helperText="This field is valid"
          />
          <Input 
            label="Error Input"
            placeholder="This input has an error"
            error
            errorText="This field is required"
          />
          <Input 
            label="Disabled Input"
            placeholder="This input is disabled"
            disabled
          />
        </div>
      </div>

      {/* Badges */}
      <div className="demo-section">
        <h2>Badges</h2>
        <div className="badge-grid">
          <div className="badge-group">
            <h3>Variants</h3>
            <Badge variant="default">Default</Badge>
            <Badge variant="primary">Primary</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="accent">Accent</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="error">Error</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
          <div className="badge-group">
            <h3>Sizes</h3>
            <Badge size="small">Small</Badge>
            <Badge size="medium">Medium</Badge>
            <Badge size="large">Large</Badge>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="demo-section">
        <h2>Alerts</h2>
        <div className="alert-grid">
          {showAlert && (
            <Alert 
              variant="info" 
              dismissible 
              onDismiss={() => setShowAlert(false)}
            >
              This is an info alert that can be dismissed.
            </Alert>
          )}
          <Alert variant="success">
            This is a success alert with important information.
          </Alert>
          <Alert variant="error">
            This is an error alert indicating something went wrong.
          </Alert>
          <Alert variant="warning">
            This is a warning alert with cautionary information.
          </Alert>
        </div>
      </div>

      {/* Spacing Demo */}
      <div className="demo-section">
        <h2>Spacing Scale</h2>
        <div className="spacing-demo">
          <div className="spacing-item" style={{ marginBottom: '4px' }}>4px spacing</div>
          <div className="spacing-item" style={{ marginBottom: '8px' }}>8px spacing</div>
          <div className="spacing-item" style={{ marginBottom: '12px' }}>12px spacing</div>
          <div className="spacing-item" style={{ marginBottom: '16px' }}>16px spacing</div>
          <div className="spacing-item" style={{ marginBottom: '24px' }}>24px spacing</div>
          <div className="spacing-item" style={{ marginBottom: '32px' }}>32px spacing</div>
        </div>
      </div>

      {/* Layout Components */}
      <div className="demo-section">
        <h2>Layout Components</h2>
        
        {/* Layout Examples */}
        <div className="demo-subsection">
          <h3>Layout Components</h3>
          <p className="text-gray-600 mb-4">Container and Flex components have been removed to avoid conflicts with Tailwind CSS.</p>
          <div className="bg-gray-100 p-4 rounded-md mb-4">
            <p>Use Tailwind's utility classes for layout instead:</p>
            <ul className="list-disc list-inside mt-2 text-sm">
              <li><code>max-w-*</code> for container widths</li>
              <li><code>flex</code>, <code>flex-col</code>, <code>flex-row</code> for flexbox</li>
              <li><code>items-center</code>, <code>justify-center</code> for alignment</li>
            </ul>
          </div>
        </div>

        {/* Grid Examples */}
        <div className="demo-subsection">
          <h3>Grid</h3>
          <Grid columns={2} gap="medium" className="mb-4">
            <Card variant="default" padding="small">Grid Item 1</Card>
            <Card variant="default" padding="small">Grid Item 2</Card>
            <Card variant="default" padding="small">Grid Item 3</Card>
            <Card variant="default" padding="small">Grid Item 4</Card>
          </Grid>
          <Grid columns={3} gap="small" className="mb-4">
            <Card variant="default" padding="small">1</Card>
            <Card variant="default" padding="small">2</Card>
            <Card variant="default" padding="small">3</Card>
          </Grid>
        </div>

        {/* Spacing Examples */}
        <div className="demo-subsection">
          <h3>Spacing</h3>
          <div className="mb-4">
            <Spacing size="small" direction="bottom">
              <Card variant="default" padding="small">Small bottom spacing</Card>
            </Spacing>
            <Spacing size="medium" direction="bottom">
              <Card variant="default" padding="small">Medium bottom spacing</Card>
            </Spacing>
            <Spacing size="large" direction="bottom">
              <Card variant="default" padding="small">Large bottom spacing</Card>
            </Spacing>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignSystemDemo;
